// Releases and staged rollout (doc 03 §3.5). Promotion is a dashboard action,
// never a CI action.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, type Me, type Page, type Release } from "../api";
import { Badge, Card, ConfirmButton, Empty, ErrorNote, Field, Modal, PageHeader, Spinner, formatTime, useAction } from "../ui";

interface FleetVersions {
  agent_versions: { version: string; count: number }[];
  release_channels: Record<string, number>;
  total: number;
}

export default function Releases({ me }: { me: Me }) {
  const [promoting, setPromoting] = useState<Release | null>(null);
  const queryClient = useQueryClient();
  const { run } = useAction();

  const { data, isLoading, error } = useQuery<Page<Release>>({
    queryKey: ["releases"],
    queryFn: () => api.get("/v1/releases"),
  });
  const { data: fleet } = useQuery<FleetVersions>({
    queryKey: ["fleet-versions"],
    queryFn: () => api.get("/v1/releases/fleet_versions"),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["releases"] });
    queryClient.invalidateQueries({ queryKey: ["fleet-versions"] });
  };

  return (
    <>
      <PageHeader
        title="Releases"
        subtitle="CI registers artifacts in dev. Promotion to beta and stable happens here, staged by percentage."
      />

      {fleet && (
        <Card title={`Fleet version histogram (${fleet.total} Outposts)`} className="mb-6">
          <div className="space-y-2">
            {fleet.agent_versions.map((entry) => (
              <div key={entry.version} className="flex items-center gap-3 text-sm">
                <span className="w-32 shrink-0 font-mono text-xs">{entry.version}</span>
                <div className="h-4 flex-1 overflow-hidden rounded bg-ink-700">
                  <div
                    className="h-full rounded bg-brand"
                    style={{ width: `${fleet.total ? (entry.count / fleet.total) * 100 : 0}%` }}
                  />
                </div>
                <span className="w-12 shrink-0 text-right tabular-nums">{entry.count}</span>
              </div>
            ))}
            <div className="pt-2 text-xs text-ink-400">
              release channels:{" "}
              {Object.entries(fleet.release_channels).map(([channel, count]) => `${channel} ${count}`).join(" · ")}
            </div>
          </div>
        </Card>
      )}

      <Card padded={false}>
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <div className="p-4"><ErrorNote error={error} /></div>
        ) : !data?.items.length ? (
          <Empty>No releases registered. CI writes these on every build.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Version</th>
                  <th>Channel</th>
                  <th>Rollout</th>
                  <th>Artifact</th>
                  <th>Promoted</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((release) => (
                  <tr key={release.id} className={release.halted ? "opacity-60" : undefined}>
                    <td className="font-medium">{release.component}</td>
                    <td className="font-mono text-xs">{release.version}</td>
                    <td>
                      <Badge tone={release.channel === "stable" ? "ok" : release.channel === "beta" ? "warn" : "idle"}>
                        {release.channel}
                      </Badge>
                      {release.halted && <Badge tone="crit" className="ml-2">halted</Badge>}
                    </td>
                    <td>
                      <div className="flex w-28 items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-600">
                          <div
                            className={release.halted ? "h-full bg-state-crit" : "h-full bg-brand"}
                            style={{ width: `${release.rollout_pct}%` }}
                          />
                        </div>
                        <span className="w-9 text-right font-mono text-xs">{release.rollout_pct}%</span>
                      </div>
                    </td>
                    <td className="max-w-xs truncate font-mono text-xs text-ink-400">
                      {release.image_tag ?? release.artifact_url ?? "—"}
                    </td>
                    <td className="text-ink-400">
                      {release.promoted_at ? `${formatTime(release.promoted_at)} · ${release.promoted_by}` : "—"}
                    </td>
                    <td className="space-x-2 text-right">
                      {me.role === "nci_admin" && (
                        <button className="btn-ghost" onClick={() => setPromoting(release)}>promote</button>
                      )}
                      {!release.halted && (
                        <ConfirmButton
                          label="halt"
                          onConfirm={() => run(async () => { await api.post(`/v1/releases/${release.id}/halt`); refresh(); }, "Rollout halted")}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {promoting && <PromoteModal release={promoting} onClose={() => setPromoting(null)} onDone={() => { setPromoting(null); refresh(); }} />}
    </>
  );
}

interface Preview {
  release: string;
  channel: string;
  rollout_pct: number;
  eligible: number;
  included: number;
  endpoints: { endpoint_id: string; service_tag: string; current_version: string | null }[];
}

function PromoteModal({ release, onClose, onDone }: { release: Release; onClose: () => void; onDone: () => void }) {
  const [channel, setChannel] = useState(release.channel);
  const [pct, setPct] = useState(release.rollout_pct || 10);
  const { run, busy } = useAction();

  const { data: preview } = useQuery<Preview>({
    queryKey: ["rollout-preview", release.id, pct],
    queryFn: () => api.get(`/v1/releases/preview/${release.id}?rollout_pct=${pct}`),
  });

  return (
    <Modal
      title={`Promote ${release.component} ${release.version}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={busy}
            onClick={() => run(async () => {
              await api.post(`/v1/releases/${release.id}/promote`, { channel, rollout_pct: pct });
              onDone();
            }, `Promoted to ${channel} at ${pct}%`)}
          >
            {busy ? "Promoting…" : "Promote"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Channel">
          <select className="input" value={channel} onChange={(e) => setChannel(e.target.value as Release["channel"])}>
            <option value="dev">dev — lab bench</option>
            <option value="beta">beta — friendly sites</option>
            <option value="stable">stable — the fleet</option>
          </select>
        </Field>
        <Field label={`Rollout: ${pct}%`} hint="Membership is deterministic per Outpost, so raising the percentage only ever adds boxes.">
          <input type="range" min={0} max={100} step={5} value={pct} className="w-full accent-brand" onChange={(e) => setPct(Number(e.target.value))} />
        </Field>
        <div className="flex gap-2">
          {[10, 50, 100].map((step) => (
            <button key={step} className="btn-secondary flex-1" onClick={() => setPct(step)}>{step}%</button>
          ))}
        </div>
        {preview && (
          <div className="rounded-md border border-ink-600 bg-ink-900 p-3 text-sm">
            <div className="mb-2">
              <span className="font-semibold text-brand-on">{preview.included}</span> of {preview.eligible} eligible
              Outposts would take this release.
            </div>
            <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
              {preview.endpoints.map((endpoint) => (
                <li key={endpoint.endpoint_id} className="flex justify-between">
                  <span className="service-tag">{endpoint.service_tag}</span>
                  <span className="text-ink-400">{endpoint.current_version ?? "unknown"}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
